import mime from 'mime';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ChangeEvent, Ref } from 'react';
import { FormattedMessage } from 'react-intl';

import { StatusBanner } from '@xing-com/banner';
import { formatBytes } from '@xing-com/crate-jobs-domain-apply-helpers';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import type { NwtTrackingProps } from '@xing-com/crate-jobs-tracking';
import { GridItem } from '@xing-com/grid';
import { IconUpload } from '@xing-com/icons';

import { FileItem } from './file-item';
import * as S from './file-upload-field.styles';
import {
  UPLOAD_ERROR_COPY_KEYS,
  DEFAULT_MAX_FILESIZE_BYTES,
  DEFAULT_ACCEPTED_MIME_TYPES,
} from './helpers';
import type { FileUpload, ErrorMsgType } from './types';

type Props = {
  origin?: string;
  onFileChange: (value: string) => void;
  maxSizeInBytes?: number;
  supportedTypes?: string;
  buttonRef: Ref<HTMLButtonElement>;
  showRequiredError: boolean;
  requiredErrorMessage: string;
  fileUploaded?: FileUpload;
  uploadFile: (file: File) => void;
  nwtProps: Omit<NwtTrackingProps, 'event' | 'eventSchema'>;
};

export const FileUploadField: React.FC<Props> = ({
  origin = 'custom_field',
  onFileChange,
  maxSizeInBytes = DEFAULT_MAX_FILESIZE_BYTES,
  supportedTypes = DEFAULT_ACCEPTED_MIME_TYPES,
  buttonRef,
  showRequiredError,
  requiredErrorMessage,
  fileUploaded,
  uploadFile,
  nwtProps,
}) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const trackNwt = useJobsNwtTracking();
  const [uploadError, setUploadError] = useState<ErrorMsgType | null>(null);
  const tipId = useId();
  const onFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      if (event.target.files === null) {
        return;
      }
      trackNwt({
        eventSchema: 'basic',
        event: 'uploaded',
        element: origin,
        ...nwtProps,
      });

      if (event.target.files.length > 1) {
        setUploadError('MAX_FILES_ALLOWED');
        trackNwt({
          eventSchema: 'basic',
          event: 'error_shown',
          ...nwtProps,
          context: `${nwtProps.context}_${origin}_upload_error_max_file_number_exceeded`,
        });
      } else {
        const file = event.target.files[0];

        if (file.size > maxSizeInBytes) {
          setUploadError('FILE_SIZE_TOO_LARGE');
          trackNwt({
            eventSchema: 'basic',
            event: 'error_shown',
            ...nwtProps,
            context: `${nwtProps.context}_${origin}_upload_error_file_size_exceeded`,
          });
        } else if (!file.type || !supportedTypes.includes(file.type)) {
          setUploadError('MIME_TYPE_NOT_SUPPORTED');
          trackNwt({
            eventSchema: 'basic',
            event: 'error_shown',
            ...nwtProps,
            context: `${nwtProps.context}_${origin}_upload_error_invalid_file_type`,
          });
        } else {
          // upload the file
          uploadFile(file);
        }
      }
      event.target.value = '';
    },
    [maxSizeInBytes, nwtProps, origin, supportedTypes, trackNwt, uploadFile]
  );

  const onUploadButtonClick = useCallback((): void => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
      trackNwt({
        eventSchema: 'basic',
        event: 'clicked',
        element: `upload_${origin}`,
        ...nwtProps,
      });
    }
  }, [nwtProps, origin, trackNwt]);

  const onDeleteClick = useCallback(() => {
    onFileChange('');
    trackNwt({
      eventSchema: 'basic',
      event: 'deleted',
      element: `saved_${origin}`,
      ...nwtProps,
    });

    if (fileUploaded?.cancelUpload) {
      fileUploaded.cancelUpload();
    }
  }, [onFileChange, trackNwt, origin, nwtProps, fileUploaded]);

  const fileTypes = useMemo(() => {
    const supportedExtension = supportedTypes
      .split(',')
      .map((type) => mime.getExtension(type));
    const uniqueExtensions = Array.from(new Set(supportedExtension));
    return uniqueExtensions.join(', ');
  }, [supportedTypes]);

  const maxSize = useMemo(() => formatBytes(maxSizeInBytes), [maxSizeInBytes]);

  useEffect(() => {
    if (
      fileUploaded?.status === 'loading-token' ||
      fileUploaded?.status === 'uploading'
    ) {
      setUploadError(null); // remove pending error banner on new upload
    } else if (fileUploaded?.status === 'error') {
      setUploadError(fileUploaded.error ?? 'GENERIC');
      trackNwt({
        eventSchema: 'basic',
        event: 'error_shown',
        ...nwtProps,
        context: `${nwtProps.context}_${origin}_upload_error_network`,
      });
    } else if (
      fileUploaded?.status === 'success' &&
      fileUploaded?.fileServerId
    ) {
      onFileChange(fileUploaded.fileServerId); // update field value on upload success
    }
  }, [
    fileUploaded?.fileServerId,
    fileUploaded?.error,
    fileUploaded?.status,
    nwtProps,
    onFileChange,
    origin,
    trackNwt,
  ]);

  const hideButton =
    fileUploaded?.status === 'loading-token' ||
    fileUploaded?.status === 'uploading' ||
    fileUploaded?.status === 'success';

  return (
    <>
      {hideButton ? null : (
        <GridItem span={12} spanMedium={6} start={0}>
          <S.UploadButton
            icon={IconUpload}
            size="medium"
            variant="tertiary"
            data-testid="upload-button"
            onClick={onUploadButtonClick}
            aria-describedby={tipId}
            innerRef={buttonRef} // ref is not working due to styled component wrapper
          >
            <FormattedMessage id="JOBS_CUSTOM_APPLY_FORM_STEP_3_UPLOAD_FILE_BUTTON" />
          </S.UploadButton>
        </GridItem>
      )}
      <GridItem span={12}>
        <S.FileFormatTip
          size="xsmall"
          lineHeight="dense"
          id={tipId}
          aria-hidden
        >
          <FormattedMessage
            id="JOBS_APPLY_UPLOAD_HINT_FORMAT_SHORT"
            values={{ maxSize, fileTypes }}
          />
        </S.FileFormatTip>
      </GridItem>
      <input
        hidden
        type="file"
        accept={supportedTypes}
        ref={inputFileRef}
        onChange={onFileInputChange}
        data-testid={`upload-input-${origin}`}
      />
      <StatusBanner
        aria-live="polite"
        display="inline"
        variant="error"
        show={!!uploadError}
        handleOnClose={() => setUploadError(null)}
      >
        {uploadError ? (
          <FormattedMessage
            id={UPLOAD_ERROR_COPY_KEYS[uploadError]}
            values={{ maxFiles: 1, maxSize, fileTypes }}
          />
        ) : null}
      </StatusBanner>
      <S.RequiredErrorBanner
        aria-live="polite"
        display="inline"
        variant="error"
        show={showRequiredError}
      >
        <FormattedMessage id={requiredErrorMessage} />
      </S.RequiredErrorBanner>
      <FileItem fileUpload={fileUploaded} onDelete={onDeleteClick} />
      <S.AriaMessage aria-live="polite">
        {fileUploaded?.status === 'success' ? (
          <FormattedMessage
            key={fileUploaded.fileServerId}
            id="JOBS_INSTANT_APPLY_UPLOAD_FILE_SUCCESS_A11Y"
            values={{ fileName: fileUploaded.file.name }}
          />
        ) : null}
      </S.AriaMessage>
    </>
  );
};
